import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Clock3,
  Crown,
  FileText,
  Inbox,
  MessageCircle,
  Plus,
  RefreshCw,
  Send,
  Smartphone,
  X,
} from "lucide-react";

import AdminConversationWorkspace from "../shared/communication/AdminConversationWorkspace";
import ConversationList from "../shared/communication/ConversationList";
import {
  createMasterWhatsAppConversation,
  fetchMasterWhatsAppSummary,
} from "../shared/communication/api";

const MASTER_FILTERS = [
  ["all", "Todos"],
  ["unread", "Sin leer"],
  ["whatsapp", "WhatsApp"],
];

function StatCard({ icon: Icon, label, value, sublabel, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    primary: "bg-brand-primary/10 text-brand-primary",
    gold: "bg-brand-gold/20 text-amber-700",
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-wide text-slate-500">
            {label}
          </p>
          <p className="mt-2 text-2xl font-black text-slate-950">{value ?? 0}</p>
          {sublabel && <p className="mt-1 text-xs text-slate-500">{sublabel}</p>}
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

function NewMasterWhatsAppModal({ open, onClose, onCreated }) {
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPhone("");
    setError("");
    setSending(false);
  }, [open]);

  if (!open) return null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!phone.trim() || sending) return;
    setSending(true);
    setError("");
    try {
      const result = await createMasterWhatsAppConversation({
        phone_number: phone.trim(),
        send_template: true,
      });
      onCreated?.(result.conversation);
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "No se pudo iniciar el chat Master WhatsApp."
      );
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[2600] flex items-center justify-center bg-slate-950/60 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-slate-950">Nuevo Master WhatsApp</h2>
            <p className="mt-1 text-sm text-slate-500">
              Inicia una conversacion enviando la plantilla aprobada <strong>Hola :)</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-100 p-2 text-slate-500 hover:bg-slate-200"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <label className="block">
            <span className="text-xs font-black uppercase tracking-wide text-slate-500">
              WhatsApp destino
            </span>
            <input
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="987654321 o +51987654321"
              className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-3 text-sm outline-none focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20"
            />
          </label>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
            Fuera de la ventana de 24h solo se envia la plantilla Meta configurada
            como <strong>WHATSAPP_MASTER_HELLO_TEMPLATE</strong>. No se envia texto libre.
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={sending || !phone.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-2 text-sm font-black text-white hover:bg-[#9d2d62] disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              Enviar Hola :)
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MasterWhatsAppDashboard({ username, userId, setActiveComponent }) {
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const [newModalOpen, setNewModalOpen] = useState(false);

  const extraParams = useMemo(
    () => ({
      conversation_type: "master_whatsapp",
      include_triage: 1,
    }),
    []
  );

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError("");
    try {
      const data = await fetchMasterWhatsAppSummary();
      setSummary(data);
    } catch (err) {
      setSummaryError(
        err?.response?.data?.error ||
          "No se pudo cargar el resumen de Master WhatsApp."
      );
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const templateConfigured = Boolean(summary?.config?.hello_template_configured);
  const phoneConfigured = Boolean(summary?.config?.master_phone_configured);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-100">
      <header className="border-b border-slate-800 bg-slate-950 text-white">
        <div className="flex flex-col gap-4 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setActiveComponent("MasterHome")}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 transition hover:bg-white/20"
              aria-label="Volver al inicio master"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-gold/20">
              <Crown className="h-6 w-6 text-brand-gold" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black sm:text-2xl">Master WhatsApp</h1>
              <p className="truncate text-sm text-white/70">
                Inbox humano para {username || "Master"} · canal externo aislado
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={loadSummary}
              className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-sm font-bold text-white hover:bg-white/20"
            >
              <RefreshCw className={`h-4 w-4 ${summaryLoading ? "animate-spin" : ""}`} />
              Actualizar
            </button>
            <button
              type="button"
              onClick={() => setNewModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-gold px-4 py-2 text-sm font-black text-slate-950 hover:bg-amber-300"
            >
              <Plus className="h-4 w-4" />
              Nuevo WhatsApp
            </button>
          </div>
        </div>
      </header>

      <section className="border-b border-slate-200 bg-slate-50 px-4 py-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={Inbox}
            label="Conversaciones abiertas"
            value={summary?.open_count}
            sublabel="Threads master_whatsapp"
            tone="primary"
          />
          <StatCard
            icon={MessageCircle}
            label="Sin leer"
            value={summary?.unread_count}
            sublabel="Requieren atencion master"
            tone={(summary?.unread_count || 0) > 0 ? "amber" : "green"}
          />
          <StatCard
            icon={Clock3}
            label="Ventana 24h abierta"
            value={summary?.window_open_count}
            sublabel="Permiten respuesta libre"
            tone="green"
          />
          <StatCard
            icon={FileText}
            label="Requieren plantilla"
            value={summary?.template_required_count}
            sublabel={templateConfigured ? "Plantilla Hola :) lista" : "Config pendiente"}
            tone={templateConfigured && phoneConfigured ? "gold" : "amber"}
          />
        </div>

        {(summaryError || !templateConfigured || !phoneConfigured) && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
            {summaryError ||
              "Configura WHATSAPP_MASTER_PHONE_ID y WHATSAPP_MASTER_HELLO_TEMPLATE antes de usar envios reales."}
          </div>
        )}
      </section>

      <main className="flex min-h-0 flex-1 overflow-hidden">
        <div className={`${selected ? "hidden lg:block" : "block"} w-full lg:w-[430px]`}>
          <ConversationList
            scope="admin"
            userId={userId}
            selectedConversationId={selected?.id}
            onSelect={setSelected}
            title="Inbox Master WhatsApp"
            subtitle="Canal externo aislado · no auto-link en v1"
            filterOptions={MASTER_FILTERS}
            extraParams={extraParams}
            searchPlaceholder="Buscar telefono o mensaje"
            limit={50}
            enableRealtime
          />
        </div>

        <div className={`${selected ? "flex" : "hidden lg:flex"} min-w-0 flex-1 flex-col p-3`}>
          {selected && (
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="mb-3 inline-flex w-fit items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm ring-1 ring-slate-200 lg:hidden"
            >
              <ArrowLeft className="h-4 w-4" />
              Inbox
            </button>
          )}

          {selected ? (
            <AdminConversationWorkspace conversation={selected} userId={userId} />
          ) : (
            <div className="flex min-h-0 flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
              <div>
                <Smartphone className="mx-auto h-12 w-12 text-slate-300" />
                <h2 className="mt-3 text-lg font-black text-slate-900">
                  Selecciona una conversacion
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Gestiona WhatsApp libre dentro de 24h o usa la plantilla Hola :).
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <NewMasterWhatsAppModal
        open={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        onCreated={(conversation) => {
          setSelected(conversation);
          loadSummary();
        }}
      />
    </div>
  );
}
